import { createStore } from 'vuex';
import axios from 'axios';
import AuthService from './authservice';
import createPersistedState from 'vuex-persistedstate';
import localforage from 'localforage';
import getPartnerListFromJwt, {
    checkTokenHasExpired,
    ErrorLog
} from './utils.js';
import router from './router/router';
import {
    handleError,
    notifyMixin
} from './utils';

var AUTH0_DOMAIN = window.AUTH0_DOMAIN
var AUTH0_CLIENTID = window.AUTH0_CLIENTID
var AUTH0_REDIRECTURI = window.AUTH0_REDIRECTURI
var AUTH0_AUDIENCE = window.AUTH0_AUDIENCE
var AUTH0_RESPONSETYPE = window.AUTH0_RESPONSETYPE
var AUTH0_SCOPE = window.AUTH0_SCOPE
var API_BASEURL = window.API_BASEURL

// Configure localforage to use IndexedDB
const storageInstance = localforage.createInstance({
    name: 'stocktaking-app',
    storeName: 'vuex_state',
    driver: localforage.INDEXEDDB
});

// Session marker to detect new sessions and clear IndexedDB
const SESSION_MARKER = 'stocktaking_session_active';

// Clear IndexedDB on new browser sessions to preserve session-only behavior
if (!sessionStorage.getItem(SESSION_MARKER)) {
    // New session detected - clear old IndexedDB data
    storageInstance.clear().catch(err => {
        console.warn('Failed to clear IndexedDB on new session:', err);
    });
    sessionStorage.setItem(SESSION_MARKER, 'true');
}

// Configure vuex-persistedstate with localforage and IndexedDB
const vuexPersist = createPersistedState({
    storage: {
        getItem: (key) => storageInstance.getItem(key),
        setItem: (key, value) => storageInstance.setItem(key, value),
        removeItem: (key) => storageInstance.removeItem(key)
    },
    reducer: (state) => {
        // Exclude non-serializable objects from persistence
        const { auth, axiosInstance, axiosConfig, ...rest } = state;
        return rest;
    }
});

function getDefaultState() {
    return {

        auth: new AuthService(AUTH0_DOMAIN, AUTH0_CLIENTID, AUTH0_REDIRECTURI,
            AUTH0_AUDIENCE, AUTH0_RESPONSETYPE, AUTH0_SCOPE),

        // partner related store
        partnerId: 0,
        partnerList: [],

        // product list
        isLoading: false,
        listLoading: false,
        productList: [],
        // so I can easily find names and prices using product numbers
        nameList: [],
        priceList: [],
        costList: [],
        vatList: [],

        selectedProduct: null,

        // api settings
        api: axios.create({
            baseURL: API_BASEURL,
            timeout: 60000,
        }),

        // this will be set by mutations.setConfig
        requestConfig: null,

        // header state
        searchTerm: "",

        // stocktaking history
        history: [],
        // The last 20 elements of history
        lastHistory: [],

        // report object, contains various statistics
        report: null,

        // report for all stock takings in a date interval
        dateIntervalReport: null,

        // report for all stock takings in a date interval
        dateIntervalSumReport: null,

        // current mode
        mode: 'M',

        // Current/last error message
        currentError: "",
    };
}

function prepareReport(report, productNames, productPrices, productCosts, productVats) {

    // Add names and product prices to stocktakinglogs
    for (var i = 0; i < report.all_stocktakings.length; i++) {
        const prodName = productNames[report.all_stocktakings[i].product_number];
        const prodPrice = productPrices[report.all_stocktakings[i].product_number];
        const prodCost = productCosts[report.all_stocktakings[i].product_number];
        const prodVat = productVats[report.all_stocktakings[i].product_number];
        report.all_stocktakings[i].product_name = prodName || "Ukjent produkt"
        report.all_stocktakings[i].product_price = prodPrice || 0
        report.all_stocktakings[i].product_cost = prodCost || 0
        report.all_stocktakings[i].product_vat = prodVat || 0
    }

    for (var i = 0; i < report.audits_last7_days.length; i++) {
        const prodName = productNames[report.audits_last7_days[i].product_number];
        const prodPrice = productPrices[report.audits_last7_days[i].product_number];
        const prodCost = productCosts[report.audits_last7_days[i].product_number];
        const prodVat = productVats[report.audits_last7_days[i].product_number];
        report.audits_last7_days[i].product_name = prodName || "Ukjent produkt"
        report.audits_last7_days[i].product_price = prodPrice || 0
        report.audits_last7_days[i].product_cost = prodCost || 0
        report.audits_last7_days[i].product_vat = prodVat || 0
    }

    for (var i = 0; i < report.receipts_last7_days.length; i++) {
        const prodName = productNames[report.receipts_last7_days[i].product_number];
        const prodPrice = productPrices[report.receipts_last7_days[i].product_number];
        const prodCost = productCosts[report.receipts_last7_days[i].product_number];
        const prodVat = productVats[report.receipts_last7_days[i].product_number];
        report.receipts_last7_days[i].product_name = prodName || "Ukjent produkt"
        report.receipts_last7_days[i].product_price = prodPrice || 0
        report.receipts_last7_days[i].product_cost = prodCost || 0
        report.receipts_last7_days[i].product_vat = prodVat || 0
    }

    for (var i = 0; i < report.losses_last7_days.length; i++) {
        const prodName = productNames[report.losses_last7_days[i].product_number];
        const prodPrice = productPrices[report.losses_last7_days[i].product_number];
        const prodCost = productCosts[report.losses_last7_days[i].product_number];
        const prodVat = productVats[report.losses_last7_days[i].product_number];
        report.losses_last7_days[i].product_name = prodName || "Ukjent produkt"
        report.losses_last7_days[i].product_price = prodPrice || 0
        report.losses_last7_days[i].product_cost = prodCost || 0
        report.losses_last7_days[i].product_vat = prodVat || 0
    }

    // Sort everything by name
    report.all_stocktakings.sort((a, b) => a.product_name.toLowerCase() > b.product_name.toLowerCase());
    report.audits_last7_days.sort((a, b) => a.product_name.toLowerCase() > b.product_name.toLowerCase());
    report.receipts_last7_days.sort((a, b) => a.product_name.toLowerCase() > b.product_name.toLowerCase());
    report.losses_last7_days.sort((a, b) => a.product_name.toLowerCase() > b.product_name.toLowerCase());

    report.all_products.sort((a, b) => a.name.toLowerCase() > b.name.toLowerCase());
    report.all_active_products.sort((a, b) => a.name.toLowerCase() > b.name.toLowerCase());
    report.products_audited_last7_days.sort((a, b) => a.name.toLowerCase() > b.name.toLowerCase());
    report.products_not_audited_last7_days.sort((a, b) => a.name.toLowerCase() > b.name.toLowerCase());

    return report;
}

function prepareDateIntervalReport(report, productNames, productPrices, productCosts, productVats) {
    for (var i = 0; i < report.length; i++) {
        const prodName = productNames[report[i].product_number];
        const prodPrice = productPrices[report[i].product_number];
        const prodCost = productCosts[report[i].product_number];
        const prodVat = productVats[report[i].product_number];
        report[i].product_name = prodName || "Ukjent produkt"
        report[i].product_price = prodPrice || 0
        report[i].product_cost = prodCost || 0
        report[i].product_vat = prodVat || 0
    }

    report.sort((a, b) => a.product_name.toLowerCase() > b.product_name.toLowerCase());

    return report;
}

function prepareDateIntervalSumReport(report, productNames, productPrices, productCosts, productVats) {
    var prodNrs = [];
    var lastStockByProdNr = {};

    // get last instance of stock taking
    for (var i = 0; i < report.length; i++) {
        var prodNr = report[i].product_number;
        var stock = report[i].current_stock;
        var timestamp = report[i].date_time;

        var stockObj = {
            timestamp: timestamp,
            product_number: prodNr,
            current_stock: stock,
        }

        if (lastStockByProdNr[prodNr]) {
            var previousTimestamp = lastStockByProdNr[prodNr].timestamp;
            var previousDate = new Date(previousTimestamp);
            var thisDate = new Date(timestamp);

            if (thisDate > previousDate) {
                lastStockByProdNr[prodNr] = stockObj;
                console.log("overriding " + prodNr);
            } else {
                console.log("skipping " + prodNr);
            }
        } else {
            prodNrs.push(prodNr);
            lastStockByProdNr[prodNr] = stockObj;
            console.log("setting " + prodNr);
        }
    }

    var report = [];
    for (var i = 0; i < prodNrs.length; i++) {
        var prodNr = prodNrs[i];
        var lastStock = lastStockByProdNr[prodNr];
        console.log("laststock: " + JSON.stringify(lastStock));

        var reportLine = {
            name: productNames[prodNr],
            current_stock: lastStock.current_stock,
            product_number: lastStock.product_number,
            price: productPrices[prodNr],
            cost: productCosts[prodNr],
            vat: productVats[prodNr],
        }

        report.push(reportLine);
    }

    return report;
}

export const store = createStore({
    plugins: [vuexPersist],
    state() {
        return getDefaultState();
    },
    getters: {
        auth: state => state.auth,
        accessToken: state => state.auth.getAccessToken(),
        partnerId: state => state.partnerId,
        partnerList: state => state.partnerList,
        productList: state => state.productList,
        productCount: state => state.productCount,
        selectedProduct: state => state.selectedProduct,
        selectedMode: state => state.mode,
        productHistory: state => productNumber => state.history[productNumber],
        lastHistory: state => state.lastHistory,
        searchTerm: state => state.searchTerm,
        isListLoading: state => state.listLoading,
        isUserAuthenticated: state => state.auth.authenticated,
        isLoading: state => state.isLoading,
        report: state => state.report,
        dateIntervalReport: state => state.dateIntervalReport,
        dateIntervalSumReport: state => state.dateIntervalSumReport,
        nameList: state => state.nameList,
        currentError: state => state.currentError,

        auth0Domain: state => state.auth0Domain,
        auth0ClientId: state => state.auth0ClientId,
        auth0RedirectUri: state => state.auth0RedirectUri,
        auth0Audience: state => state.auth0Audience,
        auth0ResponseType: state => state.auth0ResponseType,
        auth0Scope: state => state.auth0Scope
    },
    actions: {
        login({
            commit,
            state
        }) {
            state.auth.login();
        },

        logout({
            commit,
            state
        }) {
            if (confirm("Er du sikker på at du vil logge ut?")) {
                state.auth.logout();
                router.push({
                    name: "Default"
                });
                commit('resetState');
            }
        },

        async refreshAuthToken({
            commit,
            state
        }) {
            state.auth.refreshToken()
        },

        handleAuthentication({
            commit,
            state
        }) {
            state.auth.handleAuthentication()
            commit('setRequestConfig');
        },

        refreshToken({
            commit,
            state
        }) {
            commit('setRequestConfig');
        },

        setCurrentError({
            commit,
            state
        }, error) {
            console.log("setting error: " + error);
            commit('setCurrentError', error);
        },

        openProduct({
            commit,
            state
        }, obj) {
            const product = state.productList.find(product => product.id == obj.id);
            if (product == undefined) {
                handleError(e, 'Kunne ikke åpne produkt.');
            }
            commit('setOpenedProduct', product);
        },

        resetLoaders({
            commit,
            state
        }) {
            if (!(state.isLoading && state.isListLoading)) return;
            commit("resetLoaders");
        },

        setSettingsMenu({
            commit,
            state
        }, val) {
            if (val === undefined) {
                commit('setSettingsMenu', !state.isSettingsMenuOpen);
            } else {
                commit('setSettingsMenu', val);
            }
        },

        setCurrentProductUI({
            commit,
            state
        }, obj) {
            commit('setCurrentProductUI', obj)
        },

        setSelectedMode({
            commit,
            state
        }, val) {
            commit('setSelectedMode', val);
        },

        setSearchTerm({
            commit,
            state
        }, val) {
            commit('setSearchTerm', val);
        },

        setLoading({
            commit,
            state
        }, bool) {
            commit('setLoading', bool)
        },

        scanHandler({
            commit,
            state
        }, val) {
            commit('scanFinished', false);
            commit('setSettingsMenu', false);
            commit('setSearchTerm', val);
            const result = state.productList.filter(
                product => product.barcodes &&
                    product.barcodes.indexOf(val) > -1);
            commit('scanFinished', true);
            if (result.length < 0) return;
            switch (result.length) {
                case 0:
                    Vue.notify(notifyMixin()("Ukjent produkt."));
                    break;
                case 1:
                    commit('setOpenedProduct', result[0]);
                    break;
                default:
                    Vue.notify(notifyMixin("alert")("Fant mer enn ett produkt, vennligst velg ønsker produkt."));
                    router.push({
                        name: 'Search'
                    });
            }
        },

        setEditingProduct({
            commit,
            state
        }, val) {
            commit('setEditingProduct', val);
        },

        resetState({
            commit,
            state
        }) {
            commit('resetState');
        },

        // API actions
        async loadList({
            commit,
            state
        }) {
            await state.auth.refreshToken()
                .catch(e =>
                    router.push({
                        name: "Default"
                    }));

            commit('setRequestConfig');
            let endpoint = "/partners/" +
                state.partnerId + "/stocktaking/products";
            return state.api.get(endpoint, JSON.parse(state.requestConfig))
                .then(res => {
                    // To make sure that this doesn't run all the time.
                    res.data.data.forEach(item => {
                        state.nameList[item.product_number] = item.name;
                        state.priceList[item.product_number] = item.price;
                        state.costList[item.product_number] = item.cost;
                        state.vatList[item.product_number] = item.vat;
                    });
                    commit('setList', res.data.data);
                    Promise.resolve();
                })
                .catch(e => {
                    handleError(e, "Klarte ikke å laste produktlisten.");
                    Promise.reject();
                });
        },

        async createStockTaking({
            commit,
            state
        }, params) {
            if (params.sysId == 0 || (params.count == 0 && params.type != "Audit") || params.productNumber == 0) {
                return;
            }

            await state.auth.refreshToken()
                .catch(e => router.push({
                    name: "Default"
                }));

            let that = this;
            commit('setRequestConfig');
            let endpoint = "/partners/" + state.partnerId + "/stocktaking";
            return state.api.post(endpoint, params, JSON.parse(state.requestConfig))
                .then(response => {
                    that.dispatch('historyStockTaking', {
                        productNumber: params.productNumber
                    });
                    that.dispatch('loadList');
                    Promise.resolve();
                })
                .catch(e => {
                    handleError(e, "Klarte ikke å registrere oppdateringen.")
                    Promise.reject();
                });
        },

        async updateStockTaking({
            commit,
            state
        }, params) {
            if (params.sysId == 0 || params.count == 0 ||
                params.productNumber == 0) {
                return;
            }

            await state.auth.refreshToken()
                .catch(e =>
                    router.push({
                        name: "Default"
                    }));

            let that = this;
            commit('setRequestConfig');
            var endpoint = "/partners/" + state.partnerId + "/stocktacking";
            return state.api.put(endpoint, params, JSON.parse(state.requestConfig))
                .then(response => {
                    that.dispatch('historyStockTaking', {
                        productNumber: params.productNumber
                    });
                    that.dispatch('loadList');
                    Promise.resolve();
                })
                .catch(e => {
                    handleError(e, "Klarte ikke å registrere oppdateringen.")
                    Promise.reject();
                });
        },

        async deleteStockTaking({
            commit,
            state
        }, params) {
            if (params.sysId == 0 || params.count == 0 ||
                params.productNumber == 0) {
                return;
            }

            await state.auth.refreshToken()
                .catch(e => router.push({
                    name: "Default"
                }));

            let that = this;
            commit('setRequestConfig');
            let endpoint = "/partners/" + state.partnerId + "/stocktaking";
            return state.api.delete(endpoint, params, JSON.parse(state.requestConfig))
                .then(response => {
                    that.dispatch('historyStockTaking', {
                        productNumber: params.productNumber
                    });
                    that.dispatch('loadList');
                    Promise.resolve();
                })
                .catch(e => {
                    handleError(e, 'Oppdatering ble ikke registert, sjekk internett tilkoblingen.')
                    Promise.reject();
                });
        },

        async historyStockTaking({
            commit,
            state
        }, params) {
            if (!params.productNumber) {
                return;
            }

            await state.auth.refreshToken()
                .catch(e => {
                    console.error(e);
                    router.push({
                        name: "Default"
                    })
                });

            commit('setRequestConfig');

            let endpoint = "/partners/" + state.partnerId +
                "/stocktaking/history/" +
                params.productNumber /* + "?NumberOfLogs=10" */;
            return state.api.get(endpoint, JSON.parse(state.requestConfig))
                .then(response => {
                    commit('setHistory', {
                        key: params.productNumber,
                        value: response.data.data
                    });
                    Promise.resolve();
                })
                .catch(e => {
                    handleError(e, 'Historikk kunne ikke lastes inn for dette produktet.')
                    Promise.reject();
                });
        },

        async lastHistoryStockTaking({
            commit,
            state
        }) {

            await state.auth.refreshToken()
                .catch(e => {
                    console.error(e);
                    router.push({
                        name: "Default"
                    })
                });

            commit('setRequestConfig');

            let endpoint = "/partners/" + state.partnerId +
                "/stocktaking/history/" /* + "?NumberOfLogs=10" */;
            return state.api.get(endpoint, JSON.parse(state.requestConfig))
                .then(response => {
                    commit('setLastHistory', {
                        value: response.data.data
                    });
                    Promise.resolve();
                })
                .catch(e => {
                    handleError(e, 'Historikk for siste transaksjoner kunne ikke hentes.')
                    Promise.reject();
                });
        },

        async getPartnerNames({
            commit,
            state
        }) {
            await state.auth.refreshToken()
                .catch(e => {
                    console.error(e);
                    router.push({
                        name: "Default"
                    })
                });

            commit('setRequestConfig');

            let endpoint = "/partnernames";

            return state.api.get(endpoint, JSON.parse(state.requestConfig))
                .then(response => {
                    console.log("got partner names response: ")
                    console.log(response)

                    commit('setPartnerList', response.data.data);
                    Promise.resolve();
                })
                .catch(e => {
                    handleError(e, 'Rapport kunne ikke lastes.')
                    Promise.reject();
                });
        },

        async getReport({
            commit,
            state
        }) {
            await state.auth.refreshToken()
                .catch(e => {
                    console.error(e);
                    router.push({
                        name: "Default"
                    })
                });

            commit('setRequestConfig');

            let endpoint = "/partners/" + state.partnerId +
                "/stocktaking/report"

            return state.api.get(endpoint, JSON.parse(state.requestConfig))
                .then(response => {
                    console.log("got report response:")
                    console.log(response)

                    var report = prepareReport(response.data.report_data, state.nameList, state.priceList, state.costList, state.vatList);
                    commit('setReport', report);
                    Promise.resolve();
                })
                .catch(e => {
                    handleError(e, 'Rapport kunne ikke lastes.')
                    Promise.reject();
                });
        },

        async getStockTakingsInterval({
            commit, state
        }, params) {
            await state.auth.refreshToken()
                .catch(e => {
                    console.error(e);
                    router.push({
                        name: "Default"
                    })
                });

            commit('setRequestConfig');

            let endpoint = "/partners/" + state.partnerId +
                "/stocktaking/dateinterval?" +
                "type=" + params.type + "&" +
                "from=" + params.from + "&" +
                "to=" + params.to

            return state.api.get(endpoint, JSON.parse(state.requestConfig))
                .then(response => {
                    console.log("got date interval report response:")
                    console.log(response);

                    var report = prepareDateIntervalReport(response.data.data, state.nameList, state.priceList, state.costList, state.vatList);

                    commit('setDateIntervalReport', report);
                    Promise.resolve();
                })
                .catch(e => {
                    handleError(e, 'Rapport kunne ikke lastes.')
                    Promise.reject();
                })
        },

        async getStockTakingsIntervalSum({
            commit, state
        }, params) {
            await state.auth.refreshToken()
                .catch(e => {
                    console.error(e);
                    router.push({
                        name: "Default"
                    })
                });

            commit('setRequestConfig');

            let endpoint = "/partners/" + state.partnerId +
                "/stocktaking/dateinterval?" +
                "type=" + params.type + "&" +
                "from=" + params.from + "&" +
                "to=" + params.to

            return state.api.get(endpoint, JSON.parse(state.requestConfig))
                .then(response => {
                    console.log("got date interval report response:")
                    console.log(response);

                    var report = prepareDateIntervalSumReport(response.data.data, state.nameList, state.priceList, state.costList, state.vatList);

                    commit('setDateIntervalSumReport', report);
                    Promise.resolve();
                })
                .catch(e => {
                    handleError(e, 'Rapport kunne ikke lastes.')
                    Promise.reject();
                })
        },
    },

    mutations: {
        resetLoaders(state) {
            // Simply reset the loaders when you go from page to page.
            state.listLoading = false;
            state.isLoading = false;
        },

        setList(state, newList) {
            state.listLoading = true;
            state.productList = newList;
            if (state.selectedProduct) {
                state.selectedProduct = state.productList
                    .find(x => x.id == state.selectedProduct.id);
            }
            state.listLoading = false;
        },

        setRequestConfig(state) {
            let token = state.auth.getAccessToken();
            state.requestConfig = JSON.stringify({
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Accept': 'application/json',
                    'Access-Control-Allow-Origin': '*',
                    'Content-Type': 'application/json',
                }
            })
        },

        setPartnerId(state, params) {
            state.partnerId = params.partnerId;
        },

        setOpenedProduct(state, product) {
            state.selectedProduct = product;
            router.push({
                name: 'Product'
            });
            const input = document.getElementById("productCountInput")
            if (input) {
                input.focus()
            }
        },

        setSettingsMenu(state, value) {
            state.isSettingsMenuOpen = value;
        },


        setHistory(state, params) {
            //console.log("Setting history: " + JSON.stringify(params))
            state.history[params.key] = params.value;
        },

        setLastHistory(state, params) {
            state.lastHistory = params.value;
        },

        setSelectedMode(state, params) {
            state.mode = params;
        },

        setSearchTerm(state, value) {
            state.searchTerm = value;
        },

        setCurrentProductUI(state, obj) {
            state.selectedProduct = obj;
        },

        setLoading(state, bool) {
            state.isLoading = bool;
        },

        setEditingProduct(state, value) {
            state.isEditingProduct = value;
        },

        scanFinished(state, value) {
            state.isScanFinished = value;
        },

        setPartnerList(state, value) {
            state.partnerList = value;
        },

        setReport(state, value) {
            state.report = value;
        },

        setDateIntervalReport(state, value) {
            state.dateIntervalReport = value;
        },

        setDateIntervalSumReport(state, value) {
            state.dateIntervalSumReport = value;
        },

        setCurrentError(state, value) {
            state.currentError = value;
        },

        resetState(state) {
            // Reset Vuex' state
            Object.assign(state, getDefaultState());
        }
    },
});